import React, { useState } from "react";
import {
  Button, Paper, Typography, Box, Divider, Stepper, Step, StepButton,
  MobileStepper, useTheme, useMediaQuery, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from "@mui/material";
import { NotificationManager } from "react-notifications";
import { useNavigate } from "react-router-dom";
import { UpdateBottleOrder, AssignAccountBottleOrder } from "../../services/BottleOrdersService";
import ClientSelectorDialog from "../../../vlp/components/common/ClientSelectorDialog";

import {
  STEPS, buildInitialState, sanitizeDates, sectionHeadingSx, ACTION_CONFIG,
} from "./bottleOrderFormHelpers";
import ReviewPrepStep from "./ReviewPrepStep";
import ContactAddressStep from "./ContactAddressStep";
import ShippingDetailsStep from "./ShippingDetailsStep";
import CoolerStep from "./CoolerStep";
import CommentsAttachmentsStep from "./CommentsAttachmentsStep";

const ConfirmActionDialog = ({ open, onClose, onConfirm, title, message, confirmLabel, confirmColor }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
    <DialogContent>
      <Typography>{message}</Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} variant="outlined" color="secondary">Cancel</Button>
      <Button onClick={onConfirm} variant="contained" color={confirmColor || "primary"}>{confirmLabel}</Button>
    </DialogActions>
  </Dialog>
);

const EditBottleOrderOverView = ({ bottleOrderData, currentUser, expandAll = false }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const today = new Date().toISOString().split("T")[0];
  const userInitials = currentUser || bottleOrderData?.createdBy || "";

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(buildInitialState(bottleOrderData));
  const [loading, setLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null });
  const [clientDialogOpen, setClientDialogOpen] = useState(false);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.checked }));
  };

  // ── Single API call, shared by the Save button and by the quick-action buttons ──
  const updateOrder = async (data) => {
    const payload = sanitizeDates(data);
    try {
      setLoading(true);
      await UpdateBottleOrder(payload);
      NotificationManager.success("Bottle order updated successfully.");
      window.location.reload();
    } catch (error) {
      NotificationManager.error("Failed to update bottle order.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClick = async () => {
    await updateOrder(formData);
  };

  const handleCancelClick = () => navigate("/vlp/bottle-orders");

  const openConfirm = (action) => setConfirmDialog({ open: true, action });
  const closeConfirm = () => setConfirmDialog({ open: false, action: null });

  const handleClientSelect = async (client) => {
    setClientDialogOpen(false);
    if (!client || !client.accountNumber || client.accountNumber === formData.accountNumber) {
      return;
    }
    try {
      setLoading(true);
      const response = await AssignAccountBottleOrder(formData.orderNumber, client.accountNumber);
      const success = response?.results === true;
      if (success) {
        NotificationManager.success("Account information updated successfully.");
        window.location.reload();
      } else {
        NotificationManager.error("Failed to update account information. Please try again.");
      }
    } catch (error) {
      NotificationManager.error(
        error.response?.message || error.message || "Failed to update account information."
      );
    } finally {
      setLoading(false);
    }
  };

  const actionConfig = ACTION_CONFIG(userInitials, today);

  const handleConfirmAction = async () => {
    const cfg = actionConfig[confirmDialog.action];
    if (!cfg) return;
    const updated = cfg.apply(formData);
    setFormData(updated);
    closeConfirm();
    await updateOrder(updated);
  };

  if (!bottleOrderData) return null;

  const activeCfg = confirmDialog.action ? actionConfig[confirmDialog.action] : null;

  const stepProps = { formData, handleChange, handleCheckboxChange };

  const stepContent = [
    <ReviewPrepStep {...stepProps} loading={loading} openConfirm={openConfirm} />,
    <ContactAddressStep {...stepProps} loading={loading} setClientDialogOpen={setClientDialogOpen} />,
    <ShippingDetailsStep {...stepProps} />,
    <CoolerStep {...stepProps} />,
    <CommentsAttachmentsStep {...stepProps} orderNumber={bottleOrderData?.orderNumber} />,
  ];

  return (
    <>
      {activeCfg && (
        <ConfirmActionDialog
          open={confirmDialog.open}
          onClose={closeConfirm}
          onConfirm={handleConfirmAction}
          title={activeCfg.title}
          message={activeCfg.message}
          confirmLabel={activeCfg.confirmLabel}
          confirmColor={activeCfg.confirmColor}
        />
      )}

      <ClientSelectorDialog
        open={clientDialogOpen}
        onClose={() => setClientDialogOpen(false)}
        onSelect={handleClientSelect}
        multiSelect={false}
      />

      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
        {expandAll ? (
          /* ── Expanded: every section stacked, no stepper ── */
          STEPS.map((label, index) => (
            <Box key={label} sx={{ mb: index < STEPS.length - 1 ? 4 : 0 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={sectionHeadingSx}>{label}</Typography>
                <Divider sx={{ mt: 1 }} />
              </Box>
              {stepContent[index]}
            </Box>
          ))
        ) : (
        <>
        {/* ── Stepper ── */}
        {isMobile ? (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 1 }}>
              Step {activeStep + 1} of {STEPS.length}: <strong>{STEPS[activeStep]}</strong>
            </Typography>
            <MobileStepper
              variant="progress"
              steps={STEPS.length}
              activeStep={activeStep}
              sx={{ background: "transparent", p: 0 }}
              nextButton={null}
              backButton={null}
            />
          </Box>
        ) : (
          <Stepper
            nonLinear
            activeStep={activeStep}
            alternativeLabel
            sx={{ mb: 4, overflowX: "auto", pb: 1 }}
            style={{ height: "100px" }}
          >
            {STEPS.map((label, index) => (
              <Step key={label}>
                <StepButton onClick={() => setActiveStep(index)}>{label}</StepButton>
              </Step>
            ))}
          </Stepper>
        )}

        {/* ── Section Title ── */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={sectionHeadingSx}>{STEPS[activeStep]}</Typography>
          <Divider sx={{ mt: 1 }} />
        </Box>

        {/* ── Step Content ── */}
        <Box sx={{ minHeight: 200 }}>
          {stepContent[activeStep]}
        </Box>
        </>
        )}

        {/* ── Navigation ── */}
        <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            {!expandAll && (
            <>
            <Button variant="outlined" disabled={activeStep === 0 || loading} onClick={() => setActiveStep((s) => s - 1)}>
              Back
            </Button>
            {activeStep < STEPS.length - 1 && (
              <Button variant="outlined" color="primary" onClick={() => setActiveStep((s) => s + 1)} disabled={loading}>
                Next
              </Button>
            )}
            </>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" color="secondary" onClick={handleCancelClick} disabled={loading}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleSaveClick} disabled={loading}>
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </>
  );
};

export default EditBottleOrderOverView;